import { inject, NgModule } from "@angular/core";
import { ResolveFn, RouterModule, Routes } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { authGuard } from "./features/auth/guard/auth.guard";
import { HomeComponent } from "./features/home/home.component";
import { PageNotFoundComponent } from "./shared/components/page-not-found/page-not-found.component";
import { MainLayoutComponent } from "./shared/layouts/main-layout/main-layout.component";
import { SubLayoutComponent } from "./shared/layouts/sub-layout/sub-layout.component";
import { systemdownguardGuard } from "./features/auth/guard/systemdownguard.guard";
import { ServiceUnavailableComponent } from "./shared/components/service-unavailable/service-unavailable.component";
import { PaymentResultComponent } from "./features/payment/payment-result/payment-result.component";
import { CauChuyenCuaCNHComponent } from "./shared/components/pages-footer/cau-chuyen-cua-cnh/cau-chuyen-cua-cnh.component";
import { HoiDapComponent } from "./shared/components/pages-footer/hoi-dap/hoi-dap.component";
import { VendorPageComponent } from "./shared/components/pages-footer/vendor-page/vendor-page.component";
import { ContactPageComponent } from "./shared/components/pages-footer/contact-page/contact-page.component";
import { PolicyComponent } from "./shared/components/pages-footer/policy/policy.component";
import { ComingsoonComponent } from "./shared/components/comingsoon/comingsoon.component";
import { PendingChangesGuard } from './features/auth/guard/pending-changes.guard';
import {DisputeResolutionComponent} from "./features/policy/pages/dispute-resolution/dispute-resolution.component";
import {InfoPrivacyPolicyComponent} from "./features/policy/pages/info-privacy-policy/info-privacy-policy.component";
import {RegistrationPolicyComponent} from "./features/policy/pages/registration-policy/registration-policy.component";

const pageTitle = (key: string): ResolveFn<string> =>
  () => inject(TranslateService).get(key);

const routes: Routes = [
  {
    path: "",
    component: MainLayoutComponent,
    canActivate: [systemdownguardGuard],
    children: [
      {
        path: "",
        component: HomeComponent,
        title: pageTitle("common.PAGE_TITLES.HOME")
      },
    ],
  },
  {
    path: "product",
    component: MainLayoutComponent,
    canActivate: [systemdownguardGuard],
    loadChildren: () =>
      import("./features/product-list/product.module").then(
        (m) => m.ProductModule,
      ),
  },
  {
    // Trang kết quả chuẩn: VNPay redirect về đây, trạng thái do backend quyết định chứ không do URL.
    path: "payment/result",
    component: PaymentResultComponent,
    data: { status: "pending" },
  },
  {
    // Giữ lại cho các link cũ. Component vẫn hỏi lại backend nên trạng thái ở đây chỉ là gợi ý.
    path: "payment/success",
    component: PaymentResultComponent,
    data: { status: "success" },
  },
  {
    path: "payment/failed",
    component: PaymentResultComponent,
    data: { status: "failed" },
  },
  {
    path: "service-unavailable",
    component: ServiceUnavailableComponent,
    title: pageTitle("common.PAGE_TITLES.SERVICE_UNAVAILABLE")
  },
  {
    path: "auth",
    component: SubLayoutComponent,
    loadChildren: () =>
      import("./features/auth/auth.module").then((m) => m.AuthModule),
  },
  {
    path: "measurements",
    component: SubLayoutComponent,
    canActivate: [authGuard],
    loadChildren: () =>
      import("./features/measurements/measurements.module").then(
        (m) => m.MeasurementsModule,
      ),
  },
  {
    path: "customize",
    component: SubLayoutComponent,
    loadChildren: () =>
      import("./features/customize-board/customize-board.module").then(
        (m) => m.CustomizeBoardModule,
      ),
  },
  {
    path: "cart-details",
    component: MainLayoutComponent,
    canActivate: [systemdownguardGuard],
    loadChildren: () =>
      import("./features/cart-detail/cart.module").then((m) => m.CartModule),
  },
  {
    path: "order-details",
    component: MainLayoutComponent,
    canActivate: [authGuard, systemdownguardGuard],
    loadChildren: () =>
      import("./features/order-detail/order.module").then((m) => m.OrderModule),
  },
  {
    path: "payment",
    component: MainLayoutComponent,
    canActivate: [authGuard, systemdownguardGuard],
    loadChildren: () =>
      import("./features/payment/payment.module").then((m) => m.PaymentModule),
  },
  {
    path: "profiles",
    component: MainLayoutComponent,
    canActivate: [authGuard, systemdownguardGuard],
    loadChildren: () =>
      import("./features/profile/profile.module").then((m) => m.ProfileModule),
  },
  {
    path: "cau-chuyen-cua-CNH",
    component: MainLayoutComponent,
    canActivate: [systemdownguardGuard],
    children: [
      {
        path: "",
        component: CauChuyenCuaCNHComponent,
        title: pageTitle("common.PAGE_TITLES.OUR_STORY")
      },
    ],
  },
  {
    path: "hoi-dap",
    component: MainLayoutComponent,
    canActivate: [systemdownguardGuard],
    children: [
      {
        path: "",
        component: HoiDapComponent,
        title: pageTitle("common.PAGE_TITLES.FAQ")
      },
    ],
  },
  {
    path: "cac-nha-cung-cap",
    component: MainLayoutComponent,
    canActivate: [systemdownguardGuard],
    children: [
      {
        path: "",
        component: VendorPageComponent,
        title: pageTitle("common.PAGE_TITLES.VENDORS")
      },
    ],
  },
    {
        path: 'remaining-payment',
        component: MainLayoutComponent,
        canActivate: [systemdownguardGuard],
        loadChildren: () =>
            import('./features/remaining-payment/remaining-payment.module').then(
                (m) => m.RemainingPaymentModule
            ),
    },
  {
    path: "lien-he",
    component: MainLayoutComponent,
    canActivate: [systemdownguardGuard],
    children: [
      {
        path: "",
        component: ContactPageComponent,
        title: pageTitle("common.PAGE_TITLES.CONTACT")
      },
    ],
  },
 {
    path: "chinh-sach",
    component: MainLayoutComponent,
    canActivate: [systemdownguardGuard],
    children: [
      {
        path: 'RegistrationPolicy',
        component: RegistrationPolicyComponent,
        title: pageTitle("common.PAGE_TITLES.REGISTRATION_POLICY")
      },
      {
        path: 'INFOPRIVACYPOLICY',
        component: InfoPrivacyPolicyComponent,
        title: pageTitle("common.PAGE_TITLES.PRIVACY_POLICY")
      },
      {
        path: 'DISPUTERESOLUTION',
        component: DisputeResolutionComponent,
        title: pageTitle("common.PAGE_TITLES.DISPUTE_RESOLUTION")
      },

      {
        path: ":slug",
        component: PolicyComponent,
      }
    ],
  },
  {
    path: "recover/reset-password",
    redirectTo: "/auth/reset-password",
    pathMatch: "full",
  },
  {
    path: "coming-soon/:type",
    component: MainLayoutComponent,
    canActivate: [systemdownguardGuard],
    children: [
      {
        path: "",
        component: ComingsoonComponent,
        title: pageTitle("common.PAGE_TITLES.COMING_SOON")
      },
    ],
  },
  {
    path: "go/:code",
    canActivate: [(route: import('@angular/router').ActivatedRouteSnapshot) => {
      const code = route.paramMap.get('code');
      import('../environments/environment').then(env => {
        // Prepend apiURL for local dev. In prod it will be '' and hit NGINX /api
        window.location.href = env.environment.apiURL + '/api/go/' + code;
      });
      return false;
    }],
    children: []
  },
  {
    path: 'limited',
    component: MainLayoutComponent,
    canActivate: [systemdownguardGuard],
    loadChildren: () => import('./features/limited/limited.module').then(m => m.LimitedModule)
  },
  {
    path: "**",
    component: PageNotFoundComponent,
    title: pageTitle("common.PAGE_TITLES.NOT_FOUND")
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: "top",
      anchorScrolling: "enabled",
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule { }
